import React, { useState, useEffect } from 'react';

const StatsOverview = ({ sessions }) => {
  const [timeframe, setTimeframe] = useState('all');
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalShots: 0,
    avgShotsPerSession: 0,
    avgDistance: 0,
    avgBallSpeed: 0,
    avgClubSpeed: 0,
    avgSmashFactor: 0,
    mostUsedClub: '',
    longestShot: {
      distance: 0,
      club: '',
      date: null
    },
    recentTrend: 0
  });

  useEffect(() => {
    calculateStats();
  }, [sessions, timeframe]);

  const calculateStats = () => {
    if (!sessions || sessions.length === 0) {
      setStats({
        totalSessions: 0,
        totalShots: 0,
        avgShotsPerSession: 0,
        avgDistance: 0,
        avgBallSpeed: 0,
        avgClubSpeed: 0,
        avgSmashFactor: 0,
        mostUsedClub: '',
        longestShot: {
          distance: 0,
          club: '',
          date: null
        },
        recentTrend: 0
      });
      return;
    }

    // Filter sessions based on timeframe
    let filteredSessions = [...sessions];
    const now = new Date();
    
    if (timeframe === 'week') {
      const oneWeekAgo = new Date(now);
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      filteredSessions = sessions.filter(session => new Date(session.date) >= oneWeekAgo);
    } else if (timeframe === 'month') {
      const oneMonthAgo = new Date(now);
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      filteredSessions = sessions.filter(session => new Date(session.date) >= oneMonthAgo);
    } else if (timeframe === 'year') {
      const oneYearAgo = new Date(now);
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      filteredSessions = sessions.filter(session => new Date(session.date) >= oneYearAgo);
    }

    // If no sessions in the timeframe
    if (filteredSessions.length === 0) {
      setStats({
        totalSessions: 0,
        totalShots: 0,
        avgShotsPerSession: 0,
        avgDistance: 0,
        avgBallSpeed: 0,
        avgClubSpeed: 0,
        avgSmashFactor: 0,
        mostUsedClub: '',
        longestShot: {
          distance: 0,
          club: '',
          date: null
        },
        recentTrend: 0
      });
      return;
    }

    // Calculate total sessions and shots
    const totalSessions = filteredSessions.length;
    let totalShots = 0;
    let allShots = [];
    
    filteredSessions.forEach(session => {
      totalShots += session.shots.length;
      allShots = [...allShots, ...session.shots];
    });
    
    // Calculate averages
    const avgShotsPerSession = Math.round(totalShots / totalSessions);
    
    let totalDistance = 0;
    let totalBallSpeed = 0;
    let totalClubSpeed = 0;
    let totalSmashFactor = 0;
    
    allShots.forEach(shot => {
      totalDistance += shot.distance;
      totalBallSpeed += shot.ballSpeed;
      totalClubSpeed += shot.clubSpeed;
      totalSmashFactor += shot.smashFactor;
    });
    
    const avgDistance = Math.round(totalDistance / totalShots);
    const avgBallSpeed = Math.round(totalBallSpeed / totalShots);
    const avgClubSpeed = Math.round(totalClubSpeed / totalShots);
    const avgSmashFactor = parseFloat((totalSmashFactor / totalShots).toFixed(2));
    
    // Find most used club
    const clubCounts = {};
    allShots.forEach(shot => {
      if (!clubCounts[shot.club]) {
        clubCounts[shot.club] = 0;
      }
      clubCounts[shot.club]++;
    });
    
    let mostUsedClub = '';
    let maxCount = 0;
    
    Object.keys(clubCounts).forEach(club => {
      if (clubCounts[club] > maxCount) {
        maxCount = clubCounts[club];
        mostUsedClub = club;
      }
    });
    
    // Find longest shot
    let longestShot = {
      distance: 0,
      club: '',
      date: null
    };
    
    allShots.forEach(shot => {
      if (shot.distance > longestShot.distance) {
        longestShot = {
          distance: shot.distance,
          club: shot.club,
          date: shot.timestamp
        };
      }
    });
    
    // Calculate recent trend (compare last 2 sessions with previous 2)
    let recentTrend = 0;
    
    if (filteredSessions.length >= 4) {
      const sortedSessions = [...filteredSessions].sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      
      const recent2 = sortedSessions.slice(0, 2);
      const previous2 = sortedSessions.slice(2, 4);
      
      const recent2AvgDistance = recent2.reduce((sum, session) => sum + session.avgDistance, 0) / 2;
      const previous2AvgDistance = previous2.reduce((sum, session) => sum + session.avgDistance, 0) / 2;
      
      recentTrend = recent2AvgDistance - previous2AvgDistance;
    }
    
    setStats({
      totalSessions,
      totalShots,
      avgShotsPerSession,
      avgDistance,
      avgBallSpeed,
      avgClubSpeed,
      avgSmashFactor,
      mostUsedClub,
      longestShot,
      recentTrend
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="stats-overview">
      <div className="overview-header">
        <h2>Performance Overview</h2>
        
        <div className="timeframe-selector">
          <button 
            className={timeframe === 'week' ? 'active' : ''} 
            onClick={() => setTimeframe('week')}
          >
            Last Week
          </button>
          <button 
            className={timeframe === 'month' ? 'active' : ''} 
            onClick={() => setTimeframe('month')}
          >
            Last Month
          </button>
          <button 
            className={timeframe === 'year' ? 'active' : ''} 
            onClick={() => setTimeframe('year')}
          >
            Last Year
          </button>
          <button 
            className={timeframe === 'all' ? 'active' : ''} 
            onClick={() => setTimeframe('all')}
          >
            All Time
          </button>
        </div>
      </div>
      
      {stats.totalSessions === 0 ? (
        <div className="no-data-message">
          <p>No data available for the selected timeframe.</p>
          <p>Try selecting a different timeframe or upload new session data.</p>
        </div>
      ) : (
        <div className="overview-content">
          <div className="stats-summary">
            <div className="summary-card">
              <div className="summary-value">{stats.totalSessions}</div>
              <div className="summary-label">Total Sessions</div>
            </div>
            
            <div className="summary-card">
              <div className="summary-value">{stats.totalShots}</div>
              <div className="summary-label">Total Shots</div>
            </div>
            
            <div className="summary-card">
              <div className="summary-value">{stats.avgShotsPerSession}</div>
              <div className="summary-label">Avg Shots/Session</div>
            </div>
            
            <div className="summary-card">
              <div className="summary-value">{stats.avgDistance} <span className="unit">yds</span></div>
              <div className="summary-label">Avg Distance</div>
              {stats.recentTrend !== 0 && (
                <div className={`trend ${stats.recentTrend > 0 ? 'positive' : 'negative'}`}>
                  {stats.recentTrend > 0 ? '↑' : '↓'} {Math.abs(Math.round(stats.recentTrend))} yds
                </div>
              )}
            </div>
          </div>
          
          <div className="stats-details">
            <div className="details-section">
              <h3>Performance Metrics</h3>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-label">Avg Ball Speed</div>
                  <div className="metric-value">{stats.avgBallSpeed} <span className="unit">mph</span></div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-label">Avg Club Speed</div>
                  <div className="metric-value">{stats.avgClubSpeed} <span className="unit">mph</span></div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-label">Avg Smash Factor</div>
                  <div className="metric-value">{stats.avgSmashFactor}</div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-label">Most Used Club</div>
                  <div className="metric-value">{stats.mostUsedClub}</div>
                </div>
              </div>
            </div>
            
            <div className="details-section">
              <h3>Personal Records</h3>
              <div className="records-list">
                <div className="record-item">
                  <div className="record-label">Longest Shot</div>
                  <div className="record-value">
                    {stats.longestShot.distance} <span className="unit">yds</span>
                  </div>
                  <div className="record-details">
                    {stats.longestShot.club} on {formatDate(stats.longestShot.date)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsOverview;
